<template>
  <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    <article
      v-for="manager in managers"
      :key="manager.id"
      :class="
        manager.none
          ? 'flex flex-col justify-center gap-1 rounded-lg border border-dashed bg-muted/40 p-4'
          : 'flex flex-col gap-4 rounded-lg border bg-card p-4'
      "
    >
      <template v-if="manager.none">
        <span class="text-[15px] font-semibold">Sans manager</span>
        <span class="text-sm text-muted-foreground">
          {{ manager.pairs }} groupe{{ plural(manager.pairs) }} · {{ manager.mentees }} mentoré{{
            plural(manager.mentees)
          }}
          · {{ manager.sessions }} session{{ plural(manager.sessions) }}
        </span>
      </template>
      <template v-else>
        <div class="flex items-center gap-3">
          <span
            class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground"
            aria-hidden="true"
            >{{ manager.initials }}</span
          >
          <div class="flex min-w-0 flex-col">
            <span class="truncate text-[15px] font-semibold">{{ manager.name }}</span>
            <span class="text-xs text-muted-foreground">
              {{ manager.pairs }} groupe{{ plural(manager.pairs) }} · {{ manager.mentees }} mentoré{{
                plural(manager.mentees)
              }}
            </span>
          </div>
        </div>
        <dl class="grid grid-cols-2 gap-2">
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs text-muted-foreground">Sessions réalisées</dt>
            <dd class="text-xl font-semibold tabular-nums">{{ manager.sessions.toLocaleString('fr-FR') }}</dd>
          </div>
          <div class="flex flex-col gap-0.5">
            <dt class="text-xs text-muted-foreground">Note des sessions</dt>
            <dd class="text-xl font-semibold tabular-nums">{{ manager.score }}</dd>
          </div>
        </dl>
        <div v-if="manager.progress !== null" class="flex flex-col gap-1.5">
          <div class="h-2 rounded-sm bg-muted">
            <div class="h-full rounded-sm bg-primary" :style="{ width: `${manager.progress}%` }" />
          </div>
          <span class="text-xs text-muted-foreground">{{ Math.round(manager.progress) }} % du parcours réalisé</span>
        </div>
      </template>
    </article>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { DashboardChartType } from '@/features/admin/analytics/api/dashboard'

// « Par manager » (admin only): one card per manager, sorted by name, never ranked; pairs without a manager last.
const props = defineProps<{ chart: DashboardChartType }>()

const plural = (n: number) => (n > 1 ? 's' : '')
const num = (v: unknown) => (typeof v === 'number' ? v : 0)

const managers = computed(() =>
  props.chart.rows
    .map((row) => {
      const name = String(row.manager_nom ?? '')
      const score = typeof row.note === 'number' ? `${row.note.toFixed(1).replace('.', ',')} / 5` : '—'
      const progress = typeof row.avancement_pct === 'number' ? Math.max(0, Math.min(100, row.avancement_pct)) : null
      return {
        id: String(row.manager_id ?? 'none'),
        none: num(row.sans_manager) > 0,
        name,
        initials: name
          .split(/[\s.@-]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase())
          .join(''),
        pairs: num(row.binomes),
        mentees: num(row.mentores),
        sessions: num(row.sessions),
        score,
        progress,
      }
    })
    .sort((a, b) => Number(a.none) - Number(b.none) || a.name.localeCompare(b.name, 'fr')),
)
</script>
